import {FeatureFlagRowData} from './featureFlagRows';
import {getTeamOrder} from './featureFlagTeams';

// The single place row order is decided. Ordering used to live inside buildFeatureFlagRows, which
// meant two things could order rows; keeping it here leaves the builder free to just build.

export type FeatureFlagSortColumn = 'TEAM' | 'NAME' | 'COMPANIES' | 'USERS' | 'CREATED' | 'ROLLOUT';

export type FeatureFlagSortDirection = 'ASC' | 'DESC';

export interface FeatureFlagSort {
	column: FeatureFlagSortColumn;
	direction: FeatureFlagSortDirection;
}

/**
 * Team ascending is the order the list has always opened in — team by declared order, then flag name
 * — so an operator who never touches a header sees exactly what they saw before.
 */
export const DEFAULT_FEATURE_FLAG_SORT: FeatureFlagSort = {column: 'TEAM', direction: 'ASC'};

const SORT_COLUMNS: readonly FeatureFlagSortColumn[] = ['TEAM', 'NAME', 'COMPANIES', 'USERS', 'CREATED', 'ROLLOUT'];

function effectiveRolloutPercent(row: FeatureFlagRowData): number {
	return row.isRolledOut ? 100 : row.rolloutPercent;
}

/** Missing dates sort after every real one, in whichever direction is applied afterwards. */
function compareNullableIso(a: string | null, b: string | null): number {
	if (a === b) {
		return 0;
	}
	if (!a) {
		return 1;
	}
	if (!b) {
		return -1;
	}
	// Zero-padded ISO dates compare correctly as strings, so no Date parsing is needed here.
	return a < b ? -1 : 1;
}

/**
 * Comparators for the sortable columns, each ignoring direction — `sortFeatureFlagRows` flips the
 * result. A comparator returning 0 hands over to the flag-name tie-break, so no column ever leaves
 * rows in an order that depends on how the backend happened to return them.
 */
const COMPARATORS: Record<FeatureFlagSortColumn, (a: FeatureFlagRowData, b: FeatureFlagRowData) => number> = {
	// Declared team order rather than the label, so teams read in the same order as the legend, and a
	// team this client does not know sorts last (getTeamOrder's contract) instead of alphabetically.
	TEAM: (a, b) => getTeamOrder(a.teamId) - getTeamOrder(b.teamId),
	// Compares names for real rather than deferring to the tie-break: the tie-break is applied after the
	// direction flip and is always ascending, so leaning on it here would leave NAME descending
	// indistinguishable from ascending.
	NAME: (a, b) => a.name.localeCompare(b.name),
	COMPANIES: (a, b) => a.companyCount - b.companyCount,
	USERS: (a, b) => a.userCount - b.userCount,
	// The raw ISO date, never the rendered `14 Apr 2024` label: that string sorts alphabetically, which
	// would group every April together and put 2024 beside 2019. A flag with no date sorts last
	// ascending, keeping the rows that say nothing out of the way of the ones that do.
	CREATED: (a, b) => compareNullableIso(a.createdAt, b.createdAt),
	// A rolled-out flag has reached everyone, so it ranks at the ceiling regardless of what its
	// percentage was computed from before it was rolled out.
	ROLLOUT: (a, b) => effectiveRolloutPercent(a) - effectiveRolloutPercent(b),
};

/** True when this is a column the list can be sorted by. */
export function isFeatureFlagSortColumn(value: string | null | undefined): value is FeatureFlagSortColumn {
	return value != null && (SORT_COLUMNS as readonly string[]).includes(value);
}

/**
 * The sort to apply after a header is activated. Activating the column already sorted flips its
 * direction; activating a different one starts that column ascending, because arriving at a column
 * descending reads as though the list had been sorted twice.
 */
export function resolveSortToggle(current: FeatureFlagSort, column: FeatureFlagSortColumn): FeatureFlagSort {
	if (current.column !== column) {
		return {column, direction: 'ASC'};
	}
	return {column, direction: current.direction === 'ASC' ? 'DESC' : 'ASC'};
}

/**
 * Sort read back from storage. Storage only ever holds strings, so anything unrecognised — a column
 * that has since been renamed or removed, a truncated value, a stray `null` — falls back to the
 * default rather than leaving the list in an order no header claims.
 */
export function resolveStoredSort(stored: string | null | undefined): FeatureFlagSort {
	if (!stored) {
		return DEFAULT_FEATURE_FLAG_SORT;
	}
	const [column, direction] = stored.split(':');
	if (!isFeatureFlagSortColumn(column) || (direction !== 'ASC' && direction !== 'DESC')) {
		return DEFAULT_FEATURE_FLAG_SORT;
	}
	return {column, direction};
}

/** The stored form of a sort. Paired with `resolveStoredSort`, which must read whatever this writes. */
export function serialiseSort(sort: FeatureFlagSort): string {
	return `${sort.column}:${sort.direction}`;
}

/**
 * Rows in the chosen order. Returns a new array; the input is left alone, since the unsorted rows are
 * memoised upstream and sorting in place would reorder that cache.
 *
 * Flag name ascending is the tie-break under every column, and is applied after the direction flip so
 * that names stay A-Z even when the primary column is descending — a descending Companies list reading
 * Zebra before Apple within the same count looks like a bug.
 */
export function sortFeatureFlagRows(
	rows: readonly FeatureFlagRowData[],
	sort: FeatureFlagSort = DEFAULT_FEATURE_FLAG_SORT
): FeatureFlagRowData[] {
	const compare = COMPARATORS[sort.column];
	const flip = sort.direction === 'DESC' ? -1 : 1;
	return [...rows].sort((a, b) => {
		const primary = compare(a, b) * flip;
		return primary !== 0 ? primary : a.name.localeCompare(b.name);
	});
}
